/**
 * Guided tours catalog: audiences, chaining order and preconditions.
 * Keep this as the single source of truth for which tours exist and
 * which tour should be suggested next when one finishes.
 */

export type TourAudience = 'owner' | 'caregiver';

export interface TourDefinition {
  id: string;
  audience: TourAudience;
  /** tour suggested when this one finishes (same audience), if any */
  nextTourId?: string;
}

export const TOUR_CATALOG: readonly TourDefinition[] = [
  { id: 'home', audience: 'owner', nextTourId: 'patientDetail' },
  { id: 'patientDetail', audience: 'owner' },
  { id: 'caregiverShift', audience: 'caregiver' },
] as const;

export const ALL_TOUR_IDS: readonly string[] = TOUR_CATALOG.map((t) => t.id);

export function getTourDefinition(tourId: string): TourDefinition | undefined {
  return TOUR_CATALOG.find((t) => t.id === tourId);
}

/**
 * Returns the next pending tour for the same audience, or null when the
 * chain ends or the next tour was already completed/skipped.
 */
export function getNextPendingTour(
  tourId: string,
  isDone: (id: string) => boolean,
): TourDefinition | null {
  const current = getTourDefinition(tourId);
  if (!current?.nextTourId) return null;
  const next = getTourDefinition(current.nextTourId);
  if (!next || next.audience !== current.audience) return null;
  return isDone(next.id) ? null : next;
}
